import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./style.scss";
import ReactTable from "../../reusableComponents/ReactTable/ReactTable";
import { baseUrl } from "../../utils/api/apis";
import { useDispatch, useSelector } from "react-redux";
import { deleteCoupon } from "../../store/reducers/couponSlice";
import { ReactComponent as SearchNew } from "../../assets/icons/search-svgrepo-com (1).svg";
import { ReactComponent as CopyIcon } from "../../assets/icons/CopyIcon.svg";
import { ReactComponent as BrandCopyIcon } from "../../assets/icons/BrandCopyIcon.svg";

import { useNavigate } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  changeUserStatus,
  deleteUser,
  getUserList,
  resetPassword,
} from "../../utils/api/apis";
import { returnErrors } from "../../store/reducers/error";
import { setIsLoading } from "../../store/reducers/authSlice";
import moment from "moment";
import Select from "react-select";
import { statusClassMap } from "../../utils/string";
import { ReactCountryFlag } from "react-country-flag";
import { country } from "../../utils/constants/country";
import { HtmlTooltip } from "../../reusableComponents/Tooltip";
import { Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { returnMessages } from "../../store/reducers/message";
import ConfirmationModal from "../../pages/FundingEvaluation/ConfirmationModal";
import CopyToClipboard from "react-copy-to-clipboard";
import {
  setActiveAccount,
  setActiveUser,
} from "../../store/reducers/accountSlice";
import axios from "axios";
import { toast } from "react-toastify";

// Add debounce utility at the top (after imports)
function debounce(func, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

function UserListTable() {
  const dispatch = useDispatch();
  const { selectedBrand } = useSelector((state) => state.brand);
  const idToken = useSelector((state) => state.auth.idToken);

  // Table state
  const [params, setParams] = useState({
    search: "",
    auth_type: null,
    active: null,
    page: 1,
    page_size: 20,
    user_country: null,
  });
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Modal state
  const [isDeleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    useState(false);
  const [deleteConfirmationModalContent, setDeleteConfirmationModalContent] =
    useState(null);

  // Auth type options
  const authTypeOptions = [
    { label: "All", value: null },
    { label: "Email", value: "email" },
    { label: "Google", value: "google" },
  ];

  // Debounced search handler
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setParams((prev) => ({
          ...prev,
          search: value,
          page: 1,
        }));
      }, 300),
    []
  );

  // Add local state for search input value
  const [searchInput, setSearchInput] = useState("");

  // Fetch user list
  const fetchUserList = useCallback(async () => {
    dispatch(setIsLoading(true));
    const response = await getUserList(idToken, params);
    if (response?.status < 399) {
      setTableData(response?.data?.results);
      setTotalCount(response?.data?.count);
    } else {
      dispatch(returnErrors("Error getting User list"));
    }
    dispatch(setIsLoading(false));
  }, [dispatch, idToken, params]);

  useEffect(() => {
    fetchUserList();
  }, [fetchUserList]);

  // When params.search changes (e.g., from filters), sync searchInput
  useEffect(() => {
    setSearchInput(params.search || "");
  }, [params.search]);
  const handleCopyToClipboard = (text) => {
    toast("Copied email", {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      type: "success",
      theme: "dark",
      className: "custom-toast-container",
    });
  };
  const navigate = useNavigate();
  const handleRowClick = (data) => {
    navigate("/traders-info/details", { state: { data } });
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "Email",
        accessor: "email",
        Cell: ({ row, value }) => {
          const [randomColor, setRandomColor] = useState();
          const getRandomColor = `#${Math.floor(
            Math.random() * 16777215
          ).toString(16)}`;
          if (!randomColor) {
            setRandomColor(getRandomColor);
          }

          return (
            <>
              <div
                className="column_one_wrapper"
                onClick={() => handleRowClick(row.original)}
              >
                <div
                  className="sno_wrapper"
                  style={{ backgroundColor: randomColor }}
                >
                  {value.charAt(0).toUpperCase()}
                </div>
                <div>{value}</div>
                <div onClick={(e) => e.stopPropagation()}>
                  <CopyToClipboard
                    onCopy={(e) => handleCopyToClipboard(e, value)}
                    text={value}
                  >
                    <p
                      className="table_copy_button"
                      style={{
                        marginRight: "20px",
                        cursor: "pointer",
                      }}
                    >
                      {selectedBrand?.brand === "crypto_edge" ? (
                        <BrandCopyIcon />
                      ) : (
                        <CopyIcon />
                      )}
                    </p>
                  </CopyToClipboard>
                </div>
              </div>
            </>
          );
        },
      },
      {
        Header: "Name",
        accessor: "first_name",
        Cell: ({ row }) => (
          <div style={{ cursor: "pointer" }}>
            {row.original.first_name
              ? `${row.original.first_name} ${row.original.last_name}`
              : "-"}
          </div>
        ),
      },
      {
        Header: "Date joined",
        accessor: "date_joined",
        Cell: ({ value }) => moment.utc(value).format("ll"),
      },
      {
        Header: "Auth type",
        accessor: "auth_type",
        Cell: ({ value }) => (
          <div style={{ textTransform: "capitalize" }}>{value}</div>
        ),
      },
      { Header: "Contact", accessor: "contact" },
      {
        Header: "Country",
        accessor: "country",
        Cell: ({ value }) => {
          const selectedCountry = country?.find(
            (c) => c?.country === value || c?.iso === value
          );
          return (
            <div style={{ cursor: "pointer" }}>
              <HtmlTooltip
                title={selectedCountry?.country}
                placement="right"
                followCursor
                slotProps={{
                  popper: {
                    modifiers: [
                      { name: "offset", options: { offset: [0, 20] } },
                    ],
                  },
                }}
                arrow
              >
                <div>
                  <ReactCountryFlag
                    countryCode={selectedCountry?.iso}
                    svg
                    style={{ width: "2em", height: "2em" }}
                    title={selectedCountry?.country || "N/A"}
                    aria-label={selectedCountry?.country || "N/A"}
                  />
                </div>
              </HtmlTooltip>
            </div>
          );
        },
      },
      {
        Header: "Active",
        accessor: "is_active",
        Cell: ({ row, value }) => (
          <td>
            {
              <div
                className={`status_wrapper ${
                  value ? statusClassMap.succeeded : statusClassMap.expired
                }`}
              >
                {value ? "active" : "blocked"}
              </div>
            }
          </td>
        ),
      },
      {
        Header: "Action",
        accessor: "actions",
        Cell: ({ row }) => (
          <UserActionsCell
            row={row}
            idToken={idToken}
            fetchUserList={fetchUserList}
          />
        ), // extracted below
      },
    ],
    [selectedBrand, idToken, fetchUserList]
  );

  // Search handlers
  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    debouncedSetSearch(e.target.value);
  };

  const handleSetAuth = (option) => {
    setParams((prev) => ({
      ...prev,
      auth_type: option?.value === null ? null : option?.value,
      page: 1,
    }));
  };

  const handleActiveTab = (value) => {
    setParams((prev) => ({
      ...prev,
      active: prev.active === value ? null : value,
      page: 1,
    }));
  };

  return (
    <div className="table_scroll_wrapper">
      <div className="alltable">
        <div className="alltable_header">
          <div className="search_container">
            <input
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={handleSearchChange}
            />
            <div style={{ padding: "3px 10px 0 0" }}>
              <SearchNew style={{ width: "100%" }} />
            </div>
          </div>
          <div className="sort_buttons">
            <div className="mobile_dropDowns">
              <Select
                placeholder="Auth Type"
                classNamePrefix="react-select"
                options={authTypeOptions}
                value={
                  authTypeOptions.find(
                    (item) => item.value === params.auth_type
                  ) || authTypeOptions[0]
                }
                onChange={handleSetAuth}
              />
            </div>
            <div
              className="desktop_pills"
              style={{ flex: 1, display: "flex", gap: "10px" }}
            >
              {authTypeOptions.map((item) => (
                <button
                  key={item.value}
                  onClick={() => handleSetAuth(item)}
                  className={params.auth_type === item.value ? "activeBtn" : ""}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="line"></div>
            <div style={{ flex: 1, display: "flex", gap: "10px" }}>
              <button
                onClick={() => handleActiveTab("True")}
                className={params.active === "True" ? "activeBtn" : ""}
              >
                Active
              </button>
              <button
                onClick={() => handleActiveTab("False")}
                className={params.active === "False" ? "activeBtn" : ""}
              >
                Inactive
              </button>
            </div>
            <div style={{ minWidth: "200px" }}>
              <Select
                placeholder="Search by country..."
                classNamePrefix="react-select"
                options={country.map((item) => ({
                  label: item.country,
                  value: item.country,
                }))}
                value={country
                  .map((item) => ({
                    label: item.country,
                    value: item.country,
                  }))
                  .find((country) => country.country === params.user_country)}
                onChange={(option) =>
                  setParams((pre) => ({
                    ...pre,
                    user_country: option.value,
                    page: 1,
                  }))
                }
              />
            </div>
          </div>
        </div>
        <div className="table_wrapper">
          <div className="table_scroll_wrapper">
            <ReactTable
              columns={columns}
              data={tableData}
              params={params}
              setParams={setParams}
              serverSide={true}
              totalCount={totalCount}
            />
          </div>
        </div>
      </div>
      {isDeleteConfirmationModalOpen && (
        <DeleteModal
          isModalOpen={isDeleteConfirmationModalOpen}
          closeModal={() => setDeleteConfirmationModalOpen(false)}
          deleteContent={deleteConfirmationModalContent}
        />
      )}
    </div>
  );
}

// --- Extracted Action Cell for clarity ---
function UserActionsCell({ row, idToken, fetchUserList }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [userAccounts, setUserAccounts] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [confirmationModal1, setConfirmationModal1] = useState(false);
  const [data, setData] = useState();
  const [email, setEmail] = useState();
  const open = Boolean(anchorEl);

  // Fetch user accounts for the menu
  const handleUserAccounts = async (row) => {
    let config = {
      headers: { Authorization: `Bearer ${idToken}` },
    };
    const userId = await axios
      .get(
        `${baseUrl}auth/admin/search-user/?search=${row.original.email}`,
        config
      )
      .then((res) => Array.isArray(res.data) && res?.data[0]?.id);
    const userAccountsList = await axios
      .get(`${baseUrl}account/admin/account-filter/?user_id=${userId}`, config)
      .then((res) => res?.data);
    setUserAccounts(userAccountsList);
  };

  // Menu handlers
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Status change
  const handleStatus = async (e) => {
    setConfirmationModal(true);
    setData({ status: e.is_active ? "disable" : "enable" });
    setEmail(e.id);
  };
  const handleChangeStatus = async () => {
    const response = await changeUserStatus(idToken, email, data);
    if (response?.status < 399) {
      dispatch(returnMessages(response?.data?.detail, response?.status));
      fetchUserList();
    } else {
      let msg = response?.response?.data?.detail || "Something went wrong";
      dispatch(returnErrors(msg, 400));
    }
  };

  // Reset password
  const handleResetPassword = async (e) => {
    const data = { email: e.email };
    const response = await resetPassword(idToken, data);
    if (response?.status < 399) {
      dispatch(returnMessages(response?.data?.detail, response?.status));
    } else {
      let msg = response?.response?.data?.detail || "Something went wrong";
      dispatch(returnErrors(msg, 400));
    }
  };

  // Delete user
  const handleDeleteUser = async (e) => {
    setConfirmationModal1(true);
    setEmail(e.email);
  };
  const handleDeleteUserConf = async () => {
    const response = await deleteUser(idToken, email);
    if (response?.status < 399) {
      dispatch(returnMessages(response?.data?.detail, response?.status));
      fetchUserList();
    } else {
      let msg = response?.response?.data?.detail || "Something went wrong";
      dispatch(returnErrors(msg, 400));
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {confirmationModal && (
        <ConfirmationModal
          setConfirmationModal={setConfirmationModal}
          onConfirm={handleChangeStatus}
        />
      )}
      {confirmationModal1 && (
        <ConfirmationModal
          setConfirmationModal={setConfirmationModal1}
          onConfirm={handleDeleteUserConf}
        />
      )}
      <Box sx={{ display: "flex", alignItems: "center", textAlign: "center" }}>
        <Tooltip title="Account settings">
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: "150px",
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem disabled={userAccounts}>
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleUserAccounts(row);
            }}
          >
            {userAccounts && userAccounts.length === 0
              ? "No Accounts"
              : "View Metrics"}
          </div>
        </MenuItem>
        {userAccounts && (
          <div className="accounts_container">
            {userAccounts?.map((item) => (
              <MenuItem
                key={item.login_id}
                onClick={() => {
                  dispatch(setActiveUser(item));
                  dispatch(setActiveAccount(item));
                  navigate("/traders-list-2", {
                    state: { login_id: item?.login_id },
                  });
                }}
              >
                {item?.login_id}
              </MenuItem>
            ))}
          </div>
        )}
        <MenuItem onClick={() => handleStatus(row.original)}>
          {row.original.is_active ? "Block" : "Activate"}
        </MenuItem>
        {row?.original?.auth_type === "email" ? (
          <MenuItem onClick={() => handleResetPassword(row.original)}>
            Reset Password
          </MenuItem>
        ) : null}
        <MenuItem onClick={() => handleDeleteUser(row.original)}>
          Delete
        </MenuItem>
      </Menu>
    </div>
  );
}

export default UserListTable;

const DeleteModal = ({
  isModalOpen,
  closeModal,
  deleteContent,
  onConfirmDelete,
  idToken,
}) => {
  const dispatch = useDispatch();
  const handleDelete = () => {
    dispatch(deleteCoupon({ idToken: idToken, code: deleteContent }));
    closeModal();
  };

  return (
    <div className="modal">
      <div className="modal_content">
        <p>Are you sure you want to delete?</p>
        <div className="modal_btn">
          <button onClick={handleDelete}>Yes</button>
          <button onClick={closeModal}>No</button>
        </div>
      </div>
    </div>
  );
};
